import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

// In-memory cache stored outside the component
const apiCache = new Map();

export const clearApiCache = (key) => {
  if (apiCache.has(key)) {
    apiCache.delete(key);
  }
};

/**
 * A custom hook that wraps useApi to provide in-memory caching.
 * @param {string} endpoint - The API endpoint to fetch.
 * @param {number} ttl - Time To Live in milliseconds (e.g., 300000 for 5 minutes).
 */
export const useCachedApi = (endpoint, ttl = 300000) => {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(
    async (forceRefetch = false) => {
      const cacheKey = endpoint;

      if (forceRefetch) {
        apiCache.delete(cacheKey);
      }

      const cachedItem = apiCache.get(cacheKey);

      // Check if a valid, non-expired item is in the cache
      if (cachedItem && Date.now() < cachedItem.expiry) {
        setData(cachedItem.data);
        setIsLoading(false);
        return;
      }

      // If not in cache, fetch from the API
      try {
        const accessToken = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}${endpoint}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const responseData = await response.json();

        // Store the new data and an expiry timestamp in the cache
        apiCache.set(cacheKey, {
          data: responseData,
          expiry: Date.now() + ttl,
        });

        setData(responseData);
      } catch (e) {
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    },
    [endpoint, getAccessTokenSilently, ttl]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refresh = () => fetchData(true);

  return { data, isLoading, error, refresh };
};
